"use client";

import React from "react";
import { FileText, DollarSign, CreditCard, Calendar } from "lucide-react";
import { CompanyInfoCard, InfoRow } from "./CompanyInfoCard";
import { Badge } from "@/components/ui/badge";
import type { CompanyEntity } from "@/lib/types/company";
import { formatCurrency } from "@west-tennessee-consulting/ui";

interface CompanyBusinessDetailsProps {
  company: CompanyEntity;
}

export function CompanyBusinessDetails({ company }: CompanyBusinessDetailsProps) {
  // Rates show cents. Falsy (0 / blank) stays hidden, as before.
  const rate = (value: string | number | null | undefined): string | null =>
    value ? formatCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;

  const hourlyRate = rate(company.hourly_rate);
  const monthlyRate = rate(company.monthly_rate);
  const contractType = company.contract_type;
  const paymentTerms = company.payment_terms;
  const taxId = company.tax_id;

  const hasAnyData = hourlyRate || monthlyRate || contractType || paymentTerms || taxId;

  if (!hasAnyData) return null;

  return (
    <CompanyInfoCard title="Business Details" icon={FileText}>
      {contractType && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Contract Type</span>
          <Badge variant="outline">{contractType}</Badge>
        </div>
      )}

      {hourlyRate && (
        <InfoRow label="Hourly Rate" value={`${hourlyRate}/hr`} icon={DollarSign} />
      )}

      {monthlyRate && (
        <InfoRow label="Monthly Rate" value={`${monthlyRate}/mo`} icon={DollarSign} />
      )}

      {paymentTerms && (
        <InfoRow label="Payment Terms" value={paymentTerms} icon={Calendar} />
      )}

      {taxId && (
        <InfoRow label="Tax ID" value={taxId} icon={CreditCard} />
      )}
    </CompanyInfoCard>
  );
}
